import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRoomStore } from '../stores/room-store.js';
import { useAuthStore } from '../stores/auth-store.js';
import { toast } from '../components/ui/Toast.js';
import { ControlBar } from '../components/room/ControlBar.js';
import { ChatPanel } from '../components/room/ChatPanel.js';
import { LobbyPanel } from '../components/room/LobbyPanel.js';
import { ExpiryWarningBanner } from '../components/room/ExpiryWarningBanner.js';
import { Spinner } from '../components/ui/Spinner.js';
import { useRealtimeKitClient, RealtimeKitProvider } from '@cloudflare/realtimekit-react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';
import type { WsServerMessage } from '@ember/types';

export default function RoomPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const realtimekitToken = useRoomStore((s) => s.realtimekitToken);
  const room = useRoomStore((s) => s.room);
  const ws = useRoomStore((s) => s.ws);
  const preJoinPreferences = useRoomStore((s) => s.preJoinPreferences);
  const lobbyQueue = useRoomStore((s) => s.lobbyQueue);
  const setLobbyQueue = useRoomStore((s) => s.setLobbyQueue);
  const addChatMessage = useRoomStore((s) => s.addChatMessage);
  const addParticipant = useRoomStore((s) => s.addParticipant);
  const removeParticipant = useRoomStore((s) => s.removeParticipant);
  const markParticipantKicked = useRoomStore((s) => s.markParticipantKicked);
  const setRoomExpired = useRoomStore((s) => s.setRoomExpired);
  const setExpiryWarning = useRoomStore((s) => s.setExpiryWarning);
  const resetRoom = useRoomStore((s) => s.resetRoom);

  const isHost = room?.hostUserId === user?.id;

  // Official RTK React hook — returns [meetingClient, initFn]
  const [meeting, initMeeting] = useRealtimeKitClient();
  // Initialise toggle states from pre-join preferences (mic off → muted, camera off → camera off)
  const [isMuted, setIsMuted] = useState(!preJoinPreferences.audio);
  const [isCameraOff, setIsCameraOff] = useState(!preJoinPreferences.video);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // If not admitted (e.g. direct navigation), redirect to lobby
  useEffect(() => {
    if (!realtimekitToken) {
      navigate(`/r/${slug}`, { replace: true });
    }
  }, [realtimekitToken, slug, navigate]);

  // Initialise RealtimeKit once the token is available
  useEffect(() => {
    if (!realtimekitToken) return;

    initMeeting({
      authToken: realtimekitToken,
      defaults: { audio: preJoinPreferences.audio, video: preJoinPreferences.video },
    }).catch((err: unknown) => {
      console.error('Failed to initialise RealtimeKit:', err);
      toast.error('Failed to start media session. Please try rejoining.');
    });
  }, [realtimekitToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wire up WS message handlers — reuse the existing WS from the store
  useEffect(() => {
    if (!ws) return;

    ws.onMessage((msg: WsServerMessage) => {
      switch (msg.type) {
        case 'lobby_update':
          setLobbyQueue(msg.waitingParticipants);
          break;
        case 'participant_joined':
          addParticipant(msg.participant);
          break;
        case 'participant_left':
          removeParticipant(msg.participantId);
          break;
        case 'participant_kicked':
          markParticipantKicked(msg.participantId);
          break;
        case 'kicked':
          toast.error('You have been removed from this room.');
          handleLeave();
          break;
        case 'room_expiry_warning':
          setExpiryWarning(true);
          break;
        case 'room_expired':
          setRoomExpired();
          toast.error('This room has expired.');
          handleLeave();
          break;
        case 'chat_message':
          addChatMessage({
            senderId: msg.senderId,
            senderName: msg.senderName,
            text: msg.text,
            timestamp: msg.timestamp,
          });
          if (!isChatOpen) {
            setUnreadCount((c) => c + 1);
          }
          break;
        case 'error':
          toast.error(msg.message);
          break;
      }
    });

    ws.onClose(() => {
      const state = useRoomStore.getState();
      if (!state.roomExpired) {
        toast.error('Disconnected from room.');
      }
    });
    // ws ref doesn't change; onMessage/onClose replaces callbacks in-place
  }, [ws]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleLeave() {
    resetRoom(); // closes WS and clears state
    navigate('/', { replace: true });
  }

  function handleSendMessage(text: string) {
    ws?.send({ type: 'chat_message', text });
  }

  function handleAdmit(participantId: string) {
    ws?.send({ type: 'admit_participant', participantId });
  }

  function handleDeny(participantId: string) {
    ws?.send({ type: 'deny_participant', participantId });
  }

  function handleToggleMute() {
    if (meeting?.self) {
      if (isMuted) {
        void meeting.self.enableAudio();
      } else {
        void meeting.self.disableAudio();
      }
    }
    setIsMuted((m) => !m);
  }

  function handleToggleCamera() {
    if (meeting?.self) {
      if (isCameraOff) {
        void meeting.self.enableVideo();
      } else {
        void meeting.self.disableVideo();
      }
    }
    setIsCameraOff((c) => !c);
  }

  function handleOpenChat() {
    setIsChatOpen((o) => !o);
    if (!isChatOpen) setUnreadCount(0);
  }

  return (
    <div className="flex h-screen flex-col bg-neutral-950 overflow-hidden">
      <ExpiryWarningBanner />

      {/* Host: lobby approval panel */}
      {isHost && (
        <LobbyPanel
          waitingParticipants={lobbyQueue}
          onAdmit={handleAdmit}
          onDeny={handleDeny}
        />
      )}

      {/* Main content: video + optional chat */}
      <div className="flex flex-1 overflow-hidden">
        {/* Video area */}
        <div className="flex-1 relative bg-neutral-950">
          {meeting ? (
            <RealtimeKitProvider value={meeting}>
              <RtkMeeting meeting={meeting} showSetupScreen={false} mode="fill" />
            </RealtimeKitProvider>
          ) : (
            <div className="flex h-full items-center justify-center flex-col gap-4">
              <Spinner size="lg" />
              <p className="text-sm text-neutral-400">Joining call…</p>
            </div>
          )}

          {/* E2EE indicator */}
          <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1 text-xs text-green-400">
            <span>🔒</span>
            <span>E2EE</span>
          </div>
        </div>

        {/* Chat panel (slide-in) */}
        {isChatOpen && (
          <div className="w-80 shrink-0">
            <ChatPanel onSendMessage={handleSendMessage} />
          </div>
        )}
      </div>

      {/* Control bar */}
      <ControlBar
        isMuted={isMuted}
        isCameraOff={isCameraOff}
        isChatOpen={isChatOpen}
        onToggleMute={handleToggleMute}
        onToggleCamera={handleToggleCamera}
        onToggleChat={handleOpenChat}
        onLeave={handleLeave}
        unreadCount={unreadCount}
      />
    </div>
  );
}
